// Libs
import { useState, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import DocumentTextIcon from "@heroicons/react/24/outline/DocumentTextIcon";
import UserGroupIcon from "@heroicons/react/24/outline/UserGroupIcon";
import EnvelopeIcon from "@heroicons/react/24/outline/EnvelopeIcon";
import EyeIcon from "@heroicons/react/24/outline/EyeIcon";
import RocketLaunchIcon from "@heroicons/react/24/outline/RocketLaunchIcon";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import CheckIcon from "@heroicons/react/24/solid/CheckIcon";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

// Local
import HeadingOne from "../../components/HeadingOne";
import BaseInputText from "../../components/BaseInputText";
import BaseInputTextArea from "../../components/BaseInputTextArea";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import SafeEmailPreview from "../../components/EmailMarketing/SafeEmailPreview";
import {
  ExistingEmailTemplate,
  EmailTemplateStatus,
} from "../../models/email-template";
import { useToastMessageStore } from "../../store/toast-messages";
import { createToastMessageID } from "../../utils/createToastMessageID";
import { ExistingEmailCampaign } from "../../models/email-campaign";
import { SmartCustomerSelector } from "../../components/EmailMarketing/SmartCustomerSelector";
import {
  CSVCustomerUploader,
  CSVCustomerRecord,
} from "../../components/EmailMarketing/CSVCustomerUploader";
import { useCustomerSelection } from "../../hooks/useCustomerSelection";
import { ExistingCustomer } from "../../models/customer";

interface FormData {
  campaignName: string;
  description?: string;
}

interface Props {
  siteKey: string;
  templates: ExistingEmailTemplate[];
  loadingTemplates?: boolean;
  onCreateCampaign: (data: {
    siteKey: string;
    templateID: string;
    campaignName: string;
    description?: string;
    customers?: ExistingCustomer[];
    csvData?: CSVCustomerRecord[];
  }) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  onUpdateTemplateStatus?: (
    templateId: string,
    status: EmailTemplateStatus,
  ) => Promise<void>;
  preSelectedTemplateId?: string | null;
  campaigns: ExistingEmailCampaign[];
  loadingCampaigns?: boolean;
}

enum Step {
  SELECT_TEMPLATE = 1,
  SELECT_CUSTOMERS = 2,
  CAMPAIGN_DETAILS = 3,
  REVIEW = 4,
}

type CustomerSourceType = "existing" | "csv";

export default function CreateEmailCampaignPage({
  siteKey,
  templates,
  onCreateCampaign,
  onCancel,
  loading = false,
  onUpdateTemplateStatus,
  preSelectedTemplateId,
  campaigns,
}: Props) {
  const [currentStep, setCurrentStep] = useState<Step>(Step.SELECT_TEMPLATE);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(
    preSelectedTemplateId || "",
  );
  const [isTemplatePreviewExpanded, setIsTemplatePreviewExpanded] =
    useState(false);
  const [updatingTemplateStatus, setUpdatingTemplateStatus] = useState(false);

  // Customer source selection
  const [customerSource, setCustomerSource] =
    useState<CustomerSourceType>("existing");
  const [csvData, setCsvData] = useState<CSVCustomerRecord[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<
    ExistingCustomer[]
  >([]);

  const addToastMessagestore = useToastMessageStore(
    (state) => state.addToastMessage,
  );

  // Use the custom hook for customer selection (existing customers) - for managing IDs only
  const {
    selectedCustomerIds,
    handleSelectionChange,
    handleRemoveCustomer,
    clearSelection,
    selectedCount: customersWithEmailCount,
  } = useCustomerSelection();

  const { register, handleSubmit, watch } = useForm<FormData>();

  // Initialize selected template if preSelectedTemplateId is provided
  useEffect(() => {
    if (preSelectedTemplateId && templates.length > 0) {
      const templateExists = templates.find(
        (t) => t.id === preSelectedTemplateId,
      );
      if (templateExists) {
        setSelectedTemplateId(preSelectedTemplateId);
      }
    }
  }, [preSelectedTemplateId, templates]);

  const campaignName = watch("campaignName");
  const description = watch("description");

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  // Calculate total selected recipients across both sources
  const totalSelectedRecipients = useMemo(() => {
    if (customerSource === "existing") {
      return selectedCustomers.length;
    } else {
      return csvData.length;
    }
  }, [customerSource, selectedCustomers.length, csvData.length]);

  // Sort templates by most recent first
  const sortedTemplates = useMemo(
    () =>
      [...templates].sort(
        (a, b) =>
          b.timestampLastModified.toMillis() -
          a.timestampLastModified.toMillis(),
      ),
    [templates],
  );

  const steps = [
    { number: 1, name: "Select Template", icon: DocumentTextIcon },
    { number: 2, name: "Select Customers", icon: UserGroupIcon },
    { number: 3, name: "Campaign Details", icon: EnvelopeIcon },
    { number: 4, name: "Review & Send", icon: EyeIcon },
  ];

  const canProceedFromStep = (step: Step): boolean => {
    switch (step) {
      case Step.SELECT_TEMPLATE:
        return selectedTemplateId !== "";
      case Step.SELECT_CUSTOMERS:
        return totalSelectedRecipients > 0;
      case Step.CAMPAIGN_DETAILS:
        return Boolean(campaignName && campaignName.trim() !== "");
      case Step.REVIEW:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (canProceedFromStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
  };

  const handleCustomerSourceChange = (source: CustomerSourceType) => {
    setCustomerSource(source);
    // Clear selections when switching sources
    if (source === "existing") {
      setCsvData([]);
    } else {
      clearSelection();
      setSelectedCustomers([]);
    }
  };

  const handleCSVDataChange = (data: CSVCustomerRecord[]) => {
    setCsvData(data);
  };

  const handleCustomersChange = (customers: ExistingCustomer[]) => {
    setSelectedCustomers(customers);
  };

  const onSubmit = async (data: FormData) => {
    if (!selectedTemplateId || totalSelectedRecipients === 0) {
      return;
    }

    try {
      const campaignData = {
        siteKey,
        templateID: selectedTemplateId,
        campaignName: data.campaignName,
        description: data.description,
        ...(customerSource === "existing"
          ? { customers: selectedCustomers }
          : { csvData: csvData }),
      };

      await onCreateCampaign(campaignData);

      // Reset form only on success
      setCurrentStep(Step.SELECT_TEMPLATE);
      setSelectedTemplateId("");
      setCustomerSource("existing");
      clearSelection();
      setSelectedCustomers([]);
      setCsvData([]);
    } catch (error) {
      // Error handling is done in the container, but we stay on current step
      console.error("Campaign creation failed:", error);
      // Don't reset the form or navigate away on error
    }
  };

  const getStepStatus = (stepNumber: number) => {
    if (stepNumber < currentStep) return "complete";
    if (stepNumber === currentStep) return "current";
    return "upcoming";
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "N/A";
    return timestamp.toDate().toLocaleDateString();
  };

  const handleMakeTemplateActive = async (templateId: string) => {
    if (!onUpdateTemplateStatus) return;

    setUpdatingTemplateStatus(true);
    try {
      await onUpdateTemplateStatus(templateId, EmailTemplateStatus.ACTIVE);
      addToastMessagestore({
        id: createToastMessageID(),
        message: "Template status updated to active",
        type: "success",
        dialog: false,
      });
    } catch (error) {
      console.error("Error updating template status:", error);
      addToastMessagestore({
        id: createToastMessageID(),
        message: "Failed to update template status",
        type: "error",
        dialog: false,
      });
    } finally {
      setUpdatingTemplateStatus(false);
    }
  };

  const isSelectedTemplateActive = (): boolean => {
    if (!selectedTemplate) return false;
    return selectedTemplate.status === EmailTemplateStatus.ACTIVE;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case Step.SELECT_TEMPLATE:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Choose Email Template
            </h2>
            <p className="text-gray-600">
              Select the email template you want to use for this campaign.
            </p>

            {sortedTemplates.length === 0 ? (
              <div className="py-8 text-center">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No templates available
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Create your first email template to get started with
                  campaigns.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-12 gap-6">
                {/* Template List - Left Side */}
                <div className="col-span-5">
                  <h3 className="mb-3 text-sm font-medium text-gray-900">
                    Templates
                  </h3>
                  <div className="max-h-96 space-y-2 overflow-y-auto">
                    {sortedTemplates.map((template) => (
                      <div
                        key={template.id}
                        className={`cursor-pointer rounded-lg border p-3 transition-all ${
                          selectedTemplateId === template.id
                            ? "bg-primary/5 border-primary"
                            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                        onClick={() => handleTemplateSelect(template.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="min-w-0 flex-1">
                            <h4 className="truncate font-medium text-gray-900">
                              {template.name}
                            </h4>
                            <p className="mt-1 truncate text-sm text-gray-600">
                              {template.subject}
                            </p>
                            <div className="mt-2 flex items-center justify-between">
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                  template.status === "active"
                                    ? "bg-green-100 text-green-800"
                                    : template.status === "draft"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {template.status}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatDate(template.timestampLastModified)}
                              </span>
                            </div>
                          </div>
                          {selectedTemplateId === template.id && (
                            <div className="ml-2 flex-shrink-0">
                              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary">
                                <CheckIcon className="h-3 w-3 text-white" />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Template Preview - Right Side */}
                <div className="col-span-7">
                  <h3 className="mb-3 text-sm font-medium text-gray-900">
                    Preview
                  </h3>
                  {selectedTemplate ? (
                    <div className="rounded-lg border border-gray-200 bg-white">
                      {/* Template Status Warning */}
                      {!isSelectedTemplateActive() && (
                        <div className="border-b border-yellow-200 bg-yellow-50 px-4 py-3">
                          <div className="flex items-start space-x-3">
                            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-yellow-800">
                                Template is not active
                              </p>
                              <p className="mt-1 text-sm text-yellow-700">
                                This template needs to be active to create a
                                campaign. You can make it active now.
                              </p>
                              {onUpdateTemplateStatus && (
                                <div className="mt-2">
                                  <BaseButtonSecondary
                                    onClick={() =>
                                      handleMakeTemplateActive(
                                        selectedTemplate.id,
                                      )
                                    }
                                    disabled={updatingTemplateStatus}
                                    className="text-sm"
                                  >
                                    {updatingTemplateStatus
                                      ? "Updating..."
                                      : "Make Active"}
                                  </BaseButtonSecondary>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                        <h4 className="font-medium text-gray-900">
                          {selectedTemplate.name}
                        </h4>
                      </div>
                      <div className="p-4">
                        <div className="mb-3 border-b border-gray-200 pb-3">
                          <div className="mb-1 text-xs font-medium text-gray-500">
                            Subject:
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            {selectedTemplate.subject}
                          </div>
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          <SafeEmailPreview
                            htmlContent={selectedTemplate.htmlContent}
                            className="w-full rounded border border-gray-200"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
                      <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h4 className="mt-2 text-sm font-medium text-gray-900">
                        Select a template
                      </h4>
                      <p className="mt-1 text-sm text-gray-500">
                        Choose a template from the list to see a preview here.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case Step.SELECT_CUSTOMERS:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Select Recipients
              </h2>
              <p className="text-gray-600">
                Choose who will receive this email campaign.
              </p>
            </div>

            {/* Customer Source Tabs */}
            <div className="rounded-lg border border-gray-200 bg-white">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => handleCustomerSourceChange("existing")}
                    className={`border-b-2 px-6 py-4 text-sm font-medium transition-colors ${
                      customerSource === "existing"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <UserGroupIcon className="h-5 w-5" />
                      Existing Customers
                    </div>
                  </button>
                  <button
                    onClick={() => handleCustomerSourceChange("csv")}
                    className={`border-b-2 px-6 py-4 text-sm font-medium transition-colors ${
                      customerSource === "csv"
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <DocumentTextIcon className="h-5 w-5" />
                      Upload CSV
                    </div>
                  </button>
                </nav>
              </div>

              <div className="p-6">
                {customerSource === "existing" ? (
                  <div>
                    <p className="mb-4 text-sm text-gray-600">
                      Select customers from your existing customer database.
                    </p>
                    <SmartCustomerSelector
                      siteKey={siteKey}
                      selectedCustomerIds={selectedCustomerIds}
                      onSelectionChange={handleSelectionChange}
                      onCustomersChange={handleCustomersChange}
                      campaigns={campaigns}
                    />
                  </div>
                ) : (
                  <div>
                    <p className="mb-4 text-sm text-gray-600">
                      Upload a CSV file with email addresses to reach potential
                      customers.
                    </p>
                    <CSVCustomerUploader
                      onCSVData={handleCSVDataChange}
                      selectedCount={csvData.length}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case Step.CAMPAIGN_DETAILS:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Campaign Details
            </h2>
            <p className="text-gray-600">
              Provide details about your email campaign.
            </p>

            <form className="space-y-4">
              <BaseInputText
                admin={true}
                {...register("campaignName", {
                  required: "Campaign name is required",
                })}
                placeholder="Enter campaign name"
              />

              <BaseInputTextArea
                admin={true}
                {...register("description")}
                placeholder="Describe the purpose of this campaign"
                rows={3}
              />
            </form>
          </div>
        );

      case Step.REVIEW:
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Review & Send Campaign
            </h2>
            <p className="text-gray-600">
              Review your campaign details before sending.
            </p>

            <div className="space-y-4">
              {/* Campaign Details */}
              <div className="rounded-lg bg-gray-50 p-4">
                <h3 className="mb-2 font-medium text-gray-900">
                  Campaign Details
                </h3>
                <dl className="grid grid-cols-1 gap-2 text-sm">
                  <div>
                    <dt className="font-medium text-gray-700">Name:</dt>
                    <dd className="text-gray-600">{campaignName}</dd>
                  </div>
                  {description && (
                    <div>
                      <dt className="font-medium text-gray-700">
                        Description:
                      </dt>
                      <dd className="text-gray-600">{description}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Template Details with Expandable Preview */}
              <div className="rounded-lg bg-gray-50 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="font-medium text-gray-900">Email Template</h3>
                  <button
                    type="button"
                    onClick={() =>
                      setIsTemplatePreviewExpanded(!isTemplatePreviewExpanded)
                    }
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    {isTemplatePreviewExpanded
                      ? "Hide Preview"
                      : "Show Preview"}
                    {isTemplatePreviewExpanded ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>

                <dl className="mb-3 grid grid-cols-1 gap-2 text-sm">
                  <div>
                    <dt className="font-medium text-gray-700">Template:</dt>
                    <dd className="text-gray-600">{selectedTemplate?.name}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-700">Subject:</dt>
                    <dd className="text-gray-600">
                      {selectedTemplate?.subject}
                    </dd>
                  </div>
                </dl>

                {/* Expandable Email Preview */}
                {isTemplatePreviewExpanded && selectedTemplate && (
                  <div className="mt-4 rounded-lg border border-gray-200 bg-white">
                    <div className="border-b border-gray-200 bg-gray-50 px-4 py-2">
                      <h4 className="text-xs font-medium uppercase tracking-wide text-gray-700">
                        Email Preview
                      </h4>
                    </div>
                    <div className="p-4">
                      <div className="mb-3 border-b border-gray-200 pb-3">
                        <div className="mb-1 text-xs font-medium text-gray-500">
                          Subject:
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          {selectedTemplate.subject}
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        <SafeEmailPreview
                          htmlContent={selectedTemplate.htmlContent}
                          className="w-full rounded border border-gray-200"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Recipients with Tabs */}
              <div className="rounded-lg bg-gray-50 p-4">
                <h3 className="mb-4 font-medium text-gray-900">Recipients</h3>

                {/* Summary */}
                <div className="mb-4 text-sm text-gray-600">
                  <p>
                    <span className="font-medium text-green-700">
                      {totalSelectedRecipients}
                    </span>{" "}
                    recipient{totalSelectedRecipients !== 1 ? "s" : ""} will
                    receive this campaign.
                  </p>
                  <p className="mt-1">
                    Source:{" "}
                    <span className="font-medium">
                      {customerSource === "existing"
                        ? "Existing Customers"
                        : "CSV Upload"}
                    </span>
                  </p>
                </div>

                {/* Recipients List */}
                <div className="max-h-96 overflow-y-auto rounded-md border border-gray-200 bg-white">
                  <div className="border-b border-gray-200 bg-gray-50 px-4 py-2">
                    <h4 className="text-xs font-medium uppercase tracking-wide text-gray-700">
                      Selected Recipients
                    </h4>
                  </div>
                  <div className="p-4">
                    {customerSource === "existing" ? (
                      <div className="text-sm text-gray-600">
                        {selectedCustomerIds.length} existing customers selected
                        for this campaign. Customer details are managed by the
                        Smart Customer Selector.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="text-sm text-gray-600">
                          {csvData.length} email addresses from CSV upload:
                        </div>
                        {csvData.length > 0 && (
                          <div className="max-h-32 overflow-y-auto rounded border border-gray-200">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                    Email
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                    Name
                                  </th>
                                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                    Customer ID
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 bg-white">
                                {csvData.slice(0, 5).map((record, index) => (
                                  <tr key={index}>
                                    <td className="px-3 py-2 text-sm text-gray-900">
                                      {record.email}
                                    </td>
                                    <td className="px-3 py-2 text-sm text-gray-500">
                                      {record.customer_name || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-sm text-gray-500">
                                      {record.customerID || "—"}
                                    </td>
                                  </tr>
                                ))}
                                {csvData.length > 5 && (
                                  <tr>
                                    <td
                                      colSpan={3}
                                      className="px-3 py-2 text-center text-sm text-gray-500"
                                    >
                                      ... and {csvData.length - 5} more
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="mx-auto max-w-screen-2xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <HeadingOne>Create Email Campaign</HeadingOne>
        <p className="mt-2 text-gray-600">
          Follow the steps below to create and send an email campaign to your
          customers.
        </p>
      </div>

      {/* Progress Steps - Updated to match OnboardingProgress */}
      <nav aria-label="Progress" className="mb-8">
        <ol role="list" className="space-y-4 md:flex md:space-x-8 md:space-y-0">
          {steps.map((step, stepIdx) => (
            <li key={step.name} className="md:flex-1">
              <div
                className={`group flex flex-col border-l-4 py-2 pl-4 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4 ${
                  getStepStatus(step.number) === "complete"
                    ? "border-primary"
                    : getStepStatus(step.number) === "current"
                      ? "border-primary"
                      : "border-gray-200"
                }`}
              >
                <span className="text-sm font-medium">
                  <span className="flex items-center">
                    {getStepStatus(step.number) === "complete" ? (
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary">
                        <CheckIcon
                          className="h-4 w-4 text-white"
                          aria-hidden="true"
                        />
                      </span>
                    ) : getStepStatus(step.number) === "current" ? (
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-primary">
                        <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                      </span>
                    ) : (
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-gray-300">
                        <span className="text-gray-500">{step.number}</span>
                      </span>
                    )}
                    <span
                      className={`ml-3 text-sm font-medium ${
                        getStepStatus(step.number) === "complete" ||
                        getStepStatus(step.number) === "current"
                          ? "text-primary"
                          : "text-gray-500"
                      }`}
                    >
                      {step.name}
                    </span>
                  </span>
                </span>
              </div>
            </li>
          ))}
        </ol>
      </nav>

      {/* Step Content */}
      <div className="mb-8 rounded-lg border border-gray-200 bg-white p-6">
        {renderStepContent()}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <div>
          {currentStep > Step.SELECT_TEMPLATE && (
            <BaseButtonSecondary onClick={handleBack} disabled={loading}>
              Back
            </BaseButtonSecondary>
          )}
        </div>

        <div className="flex space-x-3">
          {currentStep < Step.REVIEW ? (
            <BaseButtonPrimary
              onClick={handleNext}
              disabled={
                (currentStep === Step.SELECT_TEMPLATE &&
                  (!canProceedFromStep(Step.SELECT_TEMPLATE) ||
                    !isSelectedTemplateActive())) ||
                (currentStep === Step.SELECT_CUSTOMERS &&
                  !canProceedFromStep(Step.SELECT_CUSTOMERS)) ||
                (currentStep === Step.CAMPAIGN_DETAILS &&
                  !canProceedFromStep(Step.CAMPAIGN_DETAILS))
              }
            >
              Next
            </BaseButtonPrimary>
          ) : (
            <BaseButtonPrimary
              onClick={handleSubmit(onSubmit)}
              disabled={loading}
              className="flex items-center gap-2"
            >
              <RocketLaunchIcon className="h-5 w-5" />
              {loading ? "Sending..." : "Send Campaign"}
            </BaseButtonPrimary>
          )}
        </div>
      </div>
    </div>
  );
}
